/**
 * Enterprise-grade mutation hook
 * 
 * Provides:
 * - Create/Update/Delete operations with loading states
 * - Error handling with user-friendly messages
 * - Success callbacks
 * - Optimistic updates support
 */

import { useState, useCallback, useRef } from 'react';
import { useToast } from './useToast';
import { handleError, shouldShowError, type ErrorContext } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export interface UseMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  entityName?: string;
  operation?: 'create' | 'update' | 'delete' | 'custom';
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: unknown, variables: TVariables) => void;
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export interface UseMutationResult<TData, TVariables> {
  mutate: (variables: TVariables) => Promise<TData | undefined>;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  isLoading: boolean;
  error: unknown | null;
  reset: () => void;
}

/**
 * Hook for handling mutations (create, update, delete)
 */
export function useMutation<TData, TVariables = void>(
  options: UseMutationOptions<TData, TVariables>
): UseMutationResult<TData, TVariables> {
  const {
    mutationFn,
    entityName = 'item',
    operation = 'custom',
    onSuccess,
    onError,
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const isMutatingRef = useRef(false);

  const { toast } = useToast();

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData | undefined> => {
      // Prevent duplicate calls while a mutation is in progress
      if (isMutatingRef.current) {
        logger.warn(`Mutation already in progress for ${entityName}, ignoring duplicate call`, {
          operation,
          entityName,
        });
        return undefined;
      }

      try {
        isMutatingRef.current = true;
        setIsLoading(true);
        setError(null);

        const data = await mutationFn(variables);

        // Call success callback
        if (onSuccess) {
          onSuccess(data, variables);
        }

        // Show success toast
        if (showSuccessToast) {
          const message =
            typeof successMessage === 'function'
              ? successMessage(data, variables)
              : successMessage || getDefaultSuccessMessage(operation, entityName);
          toast({
            variant: 'success',
            title: 'Success',
            description: message,
          });
        }

        // Log success
        logger.info(`Successfully ${operation}d ${entityName}`, {
          operation,
          entityName,
        });

        return data;
      } catch (err: unknown) {
        const errorContext: ErrorContext = {
          operation,
          entity: entityName,
        };

        const handledError = handleError(err, errorContext);
        setError(err);

        // Show error toast
        if (showErrorToast && shouldShowError(handledError, errorContext)) {
          const message =
            errorMessage ||
            handledError.userMessage ||
            getDefaultErrorMessage(operation, entityName);
          toast({
            variant: 'destructive',
            title: 'Error',
            description: message,
          });
        }

        // Call error callback
        if (onError) {
          onError(err, variables);
        }

        // Re-throw error for caller to handle if needed
        throw err;
      } finally {
        setIsLoading(false);
        isMutatingRef.current = false;
      }
    },
    [
      mutationFn,
      entityName,
      operation,
      onSuccess,
      onError,
      successMessage,
      errorMessage,
      showSuccessToast,
      showErrorToast,
      toast,
    ]
  );

  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      const result = await mutate(variables);
      if (result === undefined) {
        throw new Error('Mutation returned undefined');
      }
      return result;
    },
    [mutate]
  );

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    mutate,
    mutateAsync,
    isLoading,
    error,
    reset,
  };
}

/**
 * Get default success message for operation
 */
function getDefaultSuccessMessage(
  operation: string,
  entityName: string
): string {
  switch (operation) {
    case 'create':
      return `${entityName} created successfully`;
    case 'update':
      return `${entityName} updated successfully`;
    case 'delete':
      return `${entityName} deleted successfully`;
    default:
      return `Operation completed successfully`;
  }
}

/**
 * Get default error message for operation
 */
function getDefaultErrorMessage(operation: string, entityName: string): string {
  switch (operation) {
    case 'create':
      return `Failed to create ${entityName}`;
    case 'update':
      return `Failed to update ${entityName}`;
    case 'delete':
      return `Failed to delete ${entityName}`;
    default:
      return `Operation failed`;
  }
}







